import { AsyncPipe, NgIf } from '@angular/common';
import { Component, Input, NgModule, OnInit, SimpleChange, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { mixinDisabled } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarModule as MatSnackBarModule,
} from '@angular/material/legacy-snack-bar';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import { ClientMaterialService } from '@cutting/main/shared/services/client-material.service';
import { CountertopsService } from '@cutting/main/shared/services/countertops.service';
import { UserSettingsService } from '@cutting/main/shared/services/user-settings.service';
import {
  AngleProcessingDialogData,
  AngleTreatment,
  BazisMaterialHelper,
  ClampJoint,
  Countertop,
  CountertopAngleProcessingBase,
  CountertopAngleProcessingDialogComponent,
  CountertopComponentAvailability,
  CountertopEdgeBanding,
  CountertopLine,
  CountertopLinesConnectorBottomSheetComponent,
  CountertopLinesJoint,
  CountertopLinesJointBottomSheetComponent,
  CountertopLinesJointTypeEnum,
  CountertopListComponent,
  CountertopPanel,
  CountertopSchemaComponent,
  CountertopSchemaComponentRenderMode,
  CountertopSettingsComponent,
  CountertopSettingsModule as CountertopSettingsModuleBase,
  CountertopSlot,
  CuttingClientMaterial,
  CuttingMaterial,
  LegacyDialogsService,
  MasonsMitreJointParams,
  MatBaseGroupPostforming,
  NgxLocalizedNumbersService,
  OutlineEditorOptions,
  OverlayLoaderModule,
  RestrictionMasonsMitre,
  getAvailableLinesJoints,
  getAvailablePanelsJoints,
  recalculateLineJoint,
  recalculatePanelJoint,
} from 'cloud-shared-lib';
import _ from 'lodash';
import { slotAppearance } from 'projects/cloud-shared-lib/src/lib/models/cutting/countertop/countertop-render.utils';
import { CountertopUtils } from 'projects/cloud-shared-lib/src/lib/models/cutting/countertop/countertop.utils';
import { CountertopsAvailableComponentsService } from 'projects/cloud-shared-lib/src/lib/modules/countertop-settings/countertops-available-components.service';
import {
  Observable,
  catchError,
  concat,
  concatMap,
  defer,
  filter,
  map,
  of,
  share,
  tap,
  throwError,
  toArray,
} from 'rxjs';
import { CountertopConnectorDialogComponent } from './countertop-connector-dialog/countertop-connector-dialog.component';

class CountertopsComponentBase {}

const _CountertopsComponentMixinBase = mixinDisabled(CountertopsComponentBase);

@NgModule({
  imports: [CountertopSettingsModuleBase],
  exports: [CountertopSettingsModuleBase],
})
class CountertopSettingsModule {}

@Component({
  selector: 'cutting-countertops',
  templateUrl: './countertops.component.html',
  styles: [
    `
      .schema {
        width: 100%;
        height: 100%;
        display: block;
        margin: auto;
      }
    `,
  ],
  standalone: true,
  imports: [
    CountertopSettingsModule,
    MatDividerModule,
    CountertopListComponent,
    MatLegacyButtonModule,
    MatLegacyDialogModule,
    AsyncPipe,
    OverlayLoaderModule,
    NgIf,
    CountertopSchemaComponent,
    MatButtonToggleModule,
    FormsModule,
    CountertopAngleProcessingDialogComponent,
    MatBottomSheetModule,
    MatSnackBarModule,
  ],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
})
export class CountertopsComponent extends _CountertopsComponentMixinBase implements OnInit {
  @Input() orderId = 0;
  @Input() componentsAvailability?: CountertopComponentAvailability;
  @Input() outlineOptions: OutlineEditorOptions;

  countertop: Countertop;
  countertops: Countertop[] = [];
  preEditCountertop: Countertop;

  userId = LocalStorageManager.regData;

  userSettings$ = this.userSettingsService.getCuttingSettings().pipe(share());
  action$: Observable<unknown>;

  panelConnectors$ = this.userSettings$.pipe(map(s => s.countertopSettings.linesClampJoints ?? []));
  showQuantityInStock$: Observable<boolean> = this.userSettings$.pipe(map(s => s.showQuantityInStock));
  isShowMaterialsPrice$: Observable<boolean> = this.userSettings$.pipe(map(s => s.estimateSettings.showMaterialsPrice));
  postformingGroups$: Observable<MatBaseGroupPostforming[]> = this.userSettings$.pipe(map(s => s.postforming));

  isValidSettings = false;

  countertops$: Observable<Countertop[]>;

  loading$: Observable<boolean>;

  edit: Countertop;
  mode: CountertopSchemaComponentRenderMode = 'schema';

  @ViewChild(CountertopSchemaComponent, { static: true }) schema: CountertopSchemaComponent;
  @ViewChild(CountertopSettingsComponent, { static: true }) settings: CountertopSettingsComponent;
  selectedEdgeBandingSides: number[];
  selectedSlots: CountertopSlot[];

  constructor(
    private localizedNumbersService: NgxLocalizedNumbersService,
    private userSettingsService: UserSettingsService,
    private countertopsService: CountertopsService,
    private clientMaterialService: ClientMaterialService,
    private snackBar: MatSnackBar,
    private dialogs: LegacyDialogsService,
    public countertopsAvailableComponentsService: CountertopsAvailableComponentsService,
    private _bottomSheet: MatBottomSheet
  ) {
    super();
  }

  ngOnInit(): void {
    this.countertops$ = this.countertopsService.getCountertops(this.orderId).pipe(
      tap((response: Countertop[]) => {
        this.countertops = response;

        const materials = _.uniqWith(
          response.map(c => c.material),
          BazisMaterialHelper.compareMaterials
        ) as CuttingMaterial[];

        materials.forEach(m => {
          this.clientMaterialService.updateMaterials('countertop', {
            article: m.article,
            name: m.name,
            inMaterialBaseId: m.inMaterialBaseId,
            type: m.type,
            preparedForDeletion: false,
            cuttings: [],
            id: 0,
          } as CuttingClientMaterial);
        });
      }),
      share()
    );

    this.loading$ = this.countertops$.pipe(map(res => !res));
  }

  getCurrencySymbol(): string {
    return this.localizedNumbersService.getCurrentLocaleDefinition().currency;
  }

  countertopChangeHandler(item: Countertop): void {
    this.countertop = item;
    this.render(this.mode, true);
  }

  panelSplitHandler(e: { countertop: Countertop; line: CountertopLine; panel: CountertopPanel; error?: string }): void {
    if (e.error) {
      this.snackBar.open(e.error);
    }

    this.action$ = this.panelConnectors$.pipe(
      concatMap((panelConnectors: ClampJoint[]) => {
        const availableConnectors = getAvailablePanelsJoints(
          e.countertop,
          { panel1: e.panel, panel2: e.panel, connector: undefined },
          panelConnectors
        );

        if (availableConnectors.length === 0) {
          this.snackBar.open($localize`Не найдены доступные стяжки для соединения панелей`);
          return;
        }

        if (availableConnectors.length === 1) {
          return of(CountertopUtils.splitPanel(e.countertop, e.line, e.panel, availableConnectors[0]));
        }

        return this.openConnectorSelectBottomSheet(
          availableConnectors,
          $localize`Выберите стяжку для частей столешницы`
        ).pipe(
          tap((j: ClampJoint) => {
            if (j) {
              CountertopUtils.splitPanel(e.countertop, e.line, e.panel, j);
            }
          })
        );
      }),
      tap(() => {
        // из-за изменения countertop-settings перерисуется схема
        this.updateSettingsCountertop(this.countertop);
      })
    );
  }

  panelDeleteHandler(e: {
    countertop: Countertop;
    line: CountertopLine;
    panel: CountertopPanel;
    error?: string;
  }): void {
    if (e.error) {
      this.snackBar.open(e.error);
    }

    try {
      e.countertop.deletePanel(e.line.uid, e.panel.uid);
    } catch (error) {
      console.log(error);
    }

    // из-за изменения countertop-settings перерисуется схема
    this.updateSettingsCountertop(this.countertop);
  }

  panelSizeChangeHandler(e: {
    countertop: Countertop;
    line: CountertopLine;
    panel: CountertopPanel;
    error?: string;
  }): void {
    if (e.error) {
      this.snackBar.open(e.error);
    }

    this.action$ = this.panelConnectors$.pipe(
      concatMap((connectors: ClampJoint[]) => {
        const linesJoints$ =
          this.countertop.linesJoints.map(j => {
            if (j.type === CountertopLinesJointTypeEnum.None) {
              return null;
            }

            const availableConnectors = getAvailableLinesJoints(this.countertop, j, connectors);
            if (availableConnectors.length === 0) {
              this.snackBar.open($localize`Не найдены доступные стяжки для новой длины соединения столешниц`);
              return null;
            }

            if (availableConnectors.length === 1) {
              return of(recalculateLineJoint(j, availableConnectors[0]));
            }

            return this.openConnectorSelectBottomSheet(
              availableConnectors,
              `Выберите тип стяжки для столешницы ${j.male.length}×${j.male.width}`
            ).pipe(
              tap(c => {
                recalculateLineJoint(j, c);
              })
            );
          }) ?? [];

        const panelsJoints$ = this.countertop.lines.map(l => {
          if (l.panelsJoints.length === 0) {
            return null;
          }

          const availableConnectors = getAvailablePanelsJoints(this.countertop, l.panelsJoints[0], connectors);
          if (availableConnectors.length === 0) {
            this.snackBar.open(
              $localize`Не найдены доступные стяжки для новой длины соединения частей столешницы ${l.length}×${l.width}`
            );
          }

          return (
            availableConnectors.length === 1
              ? of(availableConnectors[0])
              : this.openConnectorSelectBottomSheet(
                  availableConnectors,
                  `Выберите тип стяжки для частей столешницы ${l.length}×${l.width}`
                )
          ).pipe(
            tap(c => {
              l.panelsJoints.forEach(j => {
                recalculatePanelJoint(this.countertop, j, c);
              });
            })
          );
        });

        const obs = _.compact([...linesJoints$, ...panelsJoints$]);

        return concat(...obs).pipe(
          toArray(),
          tap(() => {
            // из-за изменения countertop-settings перерисуется схема
            this.updateSettingsCountertop(this.countertop);
          })
        );
      })
    );
  }

  selectProcessing(ev: { angleNumber: number; angleProcessing: CountertopAngleProcessingBase }): void {
    this.action$ = this.dialogs
      .component<AngleProcessingDialogData, CountertopAngleProcessingBase>(
        CountertopAngleProcessingDialogComponent,
        {
          angleNumber: ev.angleNumber,
          angleProcessingAvailability: this.componentsAvailability.angleProcessingAvailability,
          countertop: this.countertop,
          outlineOptions: this.outlineOptions,
        },
        800
      )
      .pipe(
        filter(r => !!r),
        tap(e => {
          this.countertop.outline.angleProcessing[ev.angleNumber] = e as CountertopAngleProcessingBase;
          this.updateSettingsCountertop(this.countertop);
        })
      );
  }

  render(mode: CountertopSchemaComponentRenderMode, resetZoom = false): void {
    const selectedSlotAppearance = ((_slot: CountertopSlot) => ({
      fill: { color: '#ff000085' },
      stroke: { color: 'red' },
    })) as typeof slotAppearance;

    this.schema?.render(
      this.countertop,
      mode,
      {
        angleSelectors: this.componentsAvailability.angleProcessing,
        linesJointSelectors: this.componentsAvailability.connectionType,
        panelRemoving: true,
        panelSelectors: true,
        panelSplit: true,
        sideSelectors: false,
        panelsJointsSelector: this.componentsAvailability.clampJointCountChange,
        edgeBandingColors: _.mapValues(_.keyBy(this.selectedEdgeBandingSides), () => 'red'),
        slotColors: this.selectedSlots?.map(s => ({ slotId: s.id, appearance: selectedSlotAppearance })),
      },
      resetZoom
    );
  }

  edgeBandingSelectionChange(items: { sideNumber: number; edgeBanding: CountertopEdgeBanding }[]): void {
    this.selectedEdgeBandingSides = items.map(b => b.sideNumber);

    this.render(this.mode);
  }

  slotSelectionChange(slots: CountertopSlot[]): void {
    this.selectedSlots = slots;

    this.render(this.mode);
  }

  addCountertop(): void {
    this.clientMaterialService.updateMaterials('countertop', {
      article: this.countertop.material.article,
      name: this.countertop.material.name,
      inMaterialBaseId: this.countertop.material.inMaterialBaseId,
      type: this.countertop.material.type,
      preparedForDeletion: false,
      cuttings: [],
      id: 0,
    } as CuttingClientMaterial);
    this.countertops.push(_.cloneDeep(this.countertop));
  }

  saveCountertop(): void {
    const editIndex = this.countertops.indexOf(this.edit);
    _.set(this.countertops, editIndex, _.cloneDeep(this.countertop));

    this.stopEditing();
  }

  stopEditing(): void {
    this.edit = undefined;
    this.countertop.id = undefined;

    if (this.preEditCountertop) {
      this.countertop = _.cloneDeep(this.preEditCountertop);
      this.preEditCountertop = null;

      this.updateSettingsCountertop(this.countertop);
    }
  }

  deleteCountertop(item: Countertop): void {
    this.clientMaterialService.prepareForDeletion('countertop', item.material.name, item.material.article);
    _.pull(this.countertops, item);
  }

  editCountertop(item: Countertop): void {
    this.preEditCountertop = _.cloneDeep(this.countertop);

    this.edit = item;
    this.edit.has3d = false;

    this.countertop = _.cloneDeep(item);
    this.updateSettingsCountertop(this.countertop);
  }

  isDisabledItem = (item: Countertop): boolean => {
    return item === this.edit;
  };

  selectLinesJoint(event: { countertop: Countertop; joint: CountertopLinesJoint }): void {
    this.action$ = this._bottomSheet
      .open(CountertopLinesJointBottomSheetComponent)
      .afterDismissed()
      .pipe(
        filter(v => !_.isNil(v)),
        concatMap((v: CountertopLinesJointTypeEnum) =>
          this.userSettings$.pipe(
            map(s => ({
              joint: v,
              anglesSettings: s.outlineEditorOptions.angles,
              connectors: s.countertopSettings.linesClampJoints,
            }))
          )
        ),
        concatMap(val => {
          const { joint, connectors, anglesSettings } = val;

          if (val.joint === CountertopLinesJointTypeEnum.None) {
            return of({ joint: joint, anglesSettings: anglesSettings, connector: undefined });
          }

          const availableConnectors = getAvailableLinesJoints(event.countertop, event.joint, connectors);

          if (availableConnectors.length === 0 && joint !== CountertopLinesJointTypeEnum.None) {
            throw new Error($localize`Не найдены доступные стяжки для соединения столешниц`);
          }

          if (availableConnectors.length === 1) {
            const connector = availableConnectors[0];

            return of({ joint, anglesSettings, connector });
          }

          return this.openConnectorSelectBottomSheet(availableConnectors).pipe(
            map(connector => {
              if (!connector) {
                throw new Error($localize`Стяжка не выбрана`);
              }

              return { joint, anglesSettings, connector };
            })
          );
        }),
        tap(val => {
          this.changeJointType(event.countertop, event.joint, val.joint, val.anglesSettings.masonsMitre, val.connector);
        }),
        catchError(err => {
          this.snackBar.open(err.message);

          return throwError(() => err);
        })
      );
  }

  selectConnector(event: { countertop: Countertop; joint: ClampJoint }): void {
    this.action$ = this.dialogs.component(CountertopConnectorDialogComponent, event.joint, 300).pipe(
      filter(v => !_.isNil(v)),
      tap(() => {
        this.updateSettingsCountertop(this.countertop);
      })
    );
  }

  private changeJointType(
    countertop: Countertop,
    item: CountertopLinesJoint,
    type: CountertopLinesJointTypeEnum,
    masonsMitreSettings?: AngleTreatment<RestrictionMasonsMitre>,
    connector?: ClampJoint
  ): void {
    item.type = type;

    if (type === CountertopLinesJointTypeEnum.MasonsMitre) {
      item.params = {
        allowance: masonsMitreSettings.restrictions.allowance,
        radius: masonsMitreSettings.restrictions.radius,
      } as MasonsMitreJointParams;
    }

    recalculateLineJoint(item, connector);

    this.updateSettingsCountertop(countertop);
  }

  private updateSettingsCountertop(item: Countertop): void {
    this.settings.ngOnChanges({
      countertop: { previousValue: this.settings.countertop, currentValue: item } as SimpleChange,
    } as SimpleChanges);
  }

  private openConnectorSelectBottomSheet(connectors: ClampJoint[], title?: string): Observable<ClampJoint> {
    return defer(() =>
      this._bottomSheet
        .open(CountertopLinesConnectorBottomSheetComponent, { data: { title, connectors }, disableClose: true })
        .afterDismissed()
    );
  }
}
